import { useEffect, useState } from "react";
import { Lock, ShieldCheck, Mail } from "lucide-react";
import { useData } from "@/context/DataContext";
import { PageHeader, Card, SectionTitle } from "@/components/common/PageHeader";
import { Loading } from "@/pages/_shared";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import api from "@/lib/api";

const CLASS_COLORS = {
  "PUBLIC": "#64748b", "INTERNAL": "#0ea5e9", "CONFIDENTIAL": "#8b5cf6",
  "SENSITIVE FINANCIAL": "#1a5fd0", "RESTRICTED IDENTITY": "#e11d48",
};

const SECURITY = ["MFA", "Passkeys", "RBAC & least privilege", "Tenant isolation", "Encryption at rest & in transit", "Secret management", "PII masking", "Audit logs", "Consent logs", "Rate limiting", "Webhook validation", "Device & session controls"];

export default function Privacy() {
  const { snapshot } = useData();
  const [emailOptIn, setEmailOptIn] = useState(true);
  const [savingEmail, setSavingEmail] = useState(false);

  useEffect(() => {
    api.get("/me/email-preferences").then(({ data }) => setEmailOptIn(data.opt_in)).catch(() => {});
  }, []);

  const toggleEmail = async (val) => {
    setEmailOptIn(val);
    setSavingEmail(true);
    try {
      await api.post("/me/email-preferences", { opt_in: val });
      toast.success(val ? "Email updates turned on" : "Email updates turned off");
    } catch {
      setEmailOptIn(!val);
      toast.error("Could not update email preferences");
    } finally {
      setSavingEmail(false);
    }
  };

  if (!snapshot) return <Loading />;
  const pv = snapshot.privacy;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="privacy-page">
      <PageHeader eyebrow="Consent & Privacy Center" title="Privacy & Permissions"
        subtitle="You control what MMT can access. Every data source carries a classification, and every action respects your consent." />

      <Card className="p-5" data-testid="email-preferences-card">
        <SectionTitle icon={Mail}>Email Notifications</SectionTitle>
        <div className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50/60 px-4 py-3.5">
          <div>
            <div className="text-[13px] font-medium text-slate-800">AI agent briefings & account emails</div>
            <div className="mt-0.5 text-[12px] text-slate-500">Get your daily readiness plan and weekly summary from your AI workforce, plus a welcome email. Turn off anytime.</div>
          </div>
          <Switch checked={emailOptIn} disabled={savingEmail} onCheckedChange={toggleEmail} data-testid="email-optin-toggle" />
        </div>
      </Card>


      <Card className="p-5">
        <SectionTitle icon={Lock}>Data Classification</SectionTitle>
        <div className="flex flex-wrap gap-2">
          {pv.data_classes.map((c) => (
            <span key={c} className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-wider"
              style={{ borderColor: CLASS_COLORS[c] + "40", color: CLASS_COLORS[c], background: CLASS_COLORS[c] + "0d" }}>
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: CLASS_COLORS[c] }} />{c}
            </span>
          ))}
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="border-b border-slate-100 px-5 py-3.5"><SectionTitle icon={Lock}>Consent & Permissions</SectionTitle></div>
        <div className="divide-y divide-slate-100">
          {pv.consents.map((c) => (
            <div key={c.scope} className="flex items-center justify-between px-5 py-3.5" data-testid="consent-row">
              <div>
                <div className="text-[13px] font-medium text-slate-800">{c.scope}</div>
                <div className="mt-0.5 flex items-center gap-2">
                  <span className="font-mono text-[9px] font-semibold uppercase tracking-wider" style={{ color: CLASS_COLORS[c.class] }}>{c.class}</span>
                  {c.date && <span className="font-mono text-[10px] text-slate-400">granted {c.date}</span>}
                </div>
              </div>
              <Switch defaultChecked={c.granted} data-testid="consent-toggle" />
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-5">
        <SectionTitle icon={ShieldCheck}>Security & Compliance Architecture</SectionTitle>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
          {SECURITY.map((s) => (
            <div key={s} className="flex items-center gap-2 rounded-lg bg-slate-50/70 px-3 py-2 text-[12px] text-slate-600">
              <ShieldCheck size={14} className="text-emerald-500" />{s}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
